import React, { useEffect, useRef, useState } from "react";
import { FaWhatsapp } from "react-icons/fa";
import { FiDownload, FiShare2, FiLink } from "react-icons/fi";
import { FORMATS, renderCard, canvasToBlob } from "../shareCard";

// Preview + share/download for a card from shareCard.js.
// blocks: the card's text blocks; shareText / shareUrl: what goes with it.
function ShareCard({ blocks, ctaLead, shareText, shareUrl, fileName, title }) {
  const canvasRef = useRef(null);
  const [format, setFormat] = useState("square");
  const [preview, setPreview] = useState("");
  const [status, setStatus] = useState("");
  const [canShareFiles, setCanShareFiles] = useState(false);
  const ctaUrl = shareUrl.replace(/^https?:\/\//, "");
  const key = JSON.stringify([blocks, ctaLead, ctaUrl, format]);

  useEffect(() => {
    let alive = true;
    const canvas = canvasRef.current || document.createElement("canvas");
    canvasRef.current = canvas;
    renderCard(canvas, format, blocks, ctaLead, ctaUrl)
      .then((c) => {
        if (!alive) return;
        setPreview(c.toDataURL("image/png"));
        if (navigator.canShare) {
          const probe = new File([""], "probe.png", { type: "image/png" });
          setCanShareFiles(navigator.canShare({ files: [probe] }));
        }
      })
      .catch((e) => console.error("Share card:", e));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const message = `${shareText} ${shareUrl}`;
  const file = async () => {
    const blob = await canvasToBlob(canvasRef.current);
    return new File([blob], `${fileName}-${format}.png`, { type: "image/png" });
  };

  const download = async () => {
    const f = await file();
    const url = URL.createObjectURL(f);
    const a = document.createElement("a");
    a.href = url;
    a.download = f.name;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  };

  // Phones: the share sheet with the picture (WhatsApp, Status, Instagram...).
  const shareImage = async () => {
    try {
      const f = await file();
      await navigator.share({ files: [f], text: message });
    } catch (e) {
      if (e.name !== "AbortError") setStatus("Sharing didn't work here. Download the picture instead.");
    }
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(message);
      setStatus("Copied. Paste it into any chat.");
    } catch (e) {
      setStatus(message);
    }
  };

  const btn = "inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors";

  return (
    <div className="bg-sritaru-50 rounded-lg p-4 text-left">
      {title && <p className="font-semibold text-sritaru-800 mb-1">{title}</p>}
      <div className="flex gap-2 mb-3" role="tablist">
        {Object.entries(FORMATS).map(([k, f]) => (
          <button
            key={k}
            role="tab"
            aria-selected={format === k}
            onClick={() => setFormat(k)}
            className={`${btn} py-1 ${format === k ? "bg-sritaru-600 text-white" : "bg-white text-sritaru-700 border border-sritaru-200 hover:bg-sritaru-100"}`}
          >
            {f.label}
          </button>
        ))}
      </div>
      <div className="flex justify-center mb-3">
        {preview ? (
          <img
            src={preview}
            alt="Your share card"
            className={`rounded-lg shadow-md ${format === "story" ? "w-56" : "w-full max-w-xs"}`}
          />
        ) : (
          <div className="w-full max-w-xs aspect-square bg-sritaru-100 rounded-lg animate-pulse" />
        )}
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        {canShareFiles && (
          <button onClick={shareImage} className={`${btn} bg-sritaru-600 text-white hover:bg-sritaru-700 sm:col-span-2`}>
            <FiShare2 /> Share picture
          </button>
        )}
        <a
          href={`https://wa.me/?text=${encodeURIComponent(message)}`}
          target="_blank"
          rel="noopener noreferrer"
          className={`${btn} bg-[#25D366] text-white hover:bg-[#1ebe5b]`}
        >
          <FaWhatsapp /> Send on WhatsApp
        </a>
        <button onClick={download} className={`${btn} bg-earth-500 text-white hover:bg-earth-600`}>
          <FiDownload /> Download picture
        </button>
        <button onClick={copyLink} className={`${btn} bg-white text-sritaru-700 border border-sritaru-200 hover:bg-sritaru-100 sm:col-span-2`}>
          <FiLink /> Copy message and link
        </button>
      </div>
      {status && <p className="text-xs text-gray-600 mt-2 break-words">{status}</p>}
      {!canShareFiles && (
        <p className="text-xs text-gray-500 mt-2">
          For WhatsApp Status: download the Status picture, then add it from your photos.
        </p>
      )}
    </div>
  );
}

export default ShareCard;
